/*************************************************/
/* HEADER                                        */
/*************************************************/
export function header(body) {
    let headerElement;
    const headerSelector = '.header';
    const headerStickyClass = 'header-sticky';
    const headerHiddenClass = 'header-hidden';

    // Ignored travel, in px, so jitter and rubber-banding can't flip the header
    const scrollDeltaThreshold = 5;

    let lastTop = 0;
    let ticking = false;
    let paused = false;

    // Clamped to the scrollable range: Safari and macOS trackpads rubber-band past
    // both ends, reporting a position below 0 or beyond the document height. The
    // bounce back from one of those reads as a gesture the reader never made — at
    // the bottom it would spring the header open on its own.
    const getScrollTop = () => {
        const top = window.pageYOffset || document.documentElement.scrollTop;
        const max = Math.max(document.documentElement.scrollHeight - window.innerHeight, 0);

        return Math.min(Math.max(top, 0), max);
    };

    const setHeaderState = () => {
        if (paused) return;

        const top = getScrollTop();
        const delta = top - lastTop;

        if (top > headerElement.offsetHeight / 2) {
            body.classList.add(headerStickyClass);
        } else {
            body.classList.remove(headerStickyClass);
        }

        if (Math.abs(delta) < scrollDeltaThreshold) return;

        // Out of the way as soon as the header itself has been scrolled past, so a
        // short scroll near the top doesn't hide it before it has anything to
        // uncover. Focus inside the header keeps it put — it can still be tabbed into.
        const hide = delta > 0
            && top > headerElement.offsetHeight
            && !headerElement.contains(document.activeElement);

        body.classList.toggle(headerHiddenClass, hide);
        lastTop = top;
    };

    const onScroll = () => {
        if (ticking) return;

        ticking = true;
        window.requestAnimationFrame(() => {
            ticking = false;
            setHeaderState();
        });
    };

    const init = () => {
        headerElement = document.querySelector(headerSelector);
        lastTop = getScrollTop();

        setHeaderState();
        window.addEventListener('scroll', onScroll, { passive: true });

        headerElement.addEventListener('focusin', () => {
            body.classList.remove(headerHiddenClass);
        });

        // A hidden tab paints no frames, so the queued check runs first on the way
        // back with a baseline from before it was hidden — a restored scroll
        // position would read as one long scroll down and hide the header.
        document.addEventListener('visibilitychange', () => {
            if (document.visibilityState === 'visible') lastTop = getScrollTop();
        });
    };

    init();

    // Lets callers (e.g. the scroll-locked slider) force the sticky state
    // and stop this module's own scroll-based check from overriding it —
    // needed because freezing an element with position:fixed can shrink the
    // document's scrollable height and trigger a spurious scroll event that
    // would otherwise fight over the same header-sticky class.
    return {
        pause: () => {
            paused = true;
            body.classList.remove(headerHiddenClass);
        },
        // Takes the position the caller scrolled to rather than reading it back:
        // the scroll may not have landed yet, and a stale baseline reads as a
        // gesture large enough to hide the header the moment the lock lets go.
        resume: (top) => {
            paused = false;
            lastTop = top ?? getScrollTop();
            setHeaderState();
        },
    };
}
